import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { inicioFimMesCorrenteLocalCp } from "../helpers/contas-pagar-datas";
import { mapContasPagarApiToView } from "../helpers/map-contas-pagar-api-to-view";
import { listarContasPagar } from "../services/contas-pagar.service";
import type {
  ContasPagarApiResponse,
  ContasPagarFiltros,
  ContasPagarView,
} from "../types/contas-pagar.types";

function filtrosPadrao(eventoId?: number): ContasPagarFiltros {
  if (eventoId != null && eventoId >= 1) {
    return {
      status: "",
      vencimentoInicio: "",
      vencimentoFim: "",
      categoria: "",
      contexto: "EVENTO",
      naturezaDespesa: "",
      centroCustoId: "",
      fornecedorId: "",
      busca: "",
      apenasVencidos: false,
      eventoId,
      page: 1,
      limit: 20,
    };
  }
  const { vencimentoInicio, vencimentoFim } = inicioFimMesCorrenteLocalCp();
  return {
    status: "",
    vencimentoInicio,
    vencimentoFim,
    categoria: "",
    contexto: "",
    naturezaDespesa: "",
    centroCustoId: "",
    fornecedorId: "",
    busca: "",
    apenasVencidos: false,
    page: 1,
    limit: 20,
  };
}

export type UseContasPagarResult = {
  filtros: ContasPagarFiltros;
  setFiltros: Dispatch<SetStateAction<ContasPagarFiltros>>;
  filtrosAplicados: ContasPagarFiltros;
  data: ContasPagarView | null;
  rawData: ContasPagarApiResponse | null;
  loading: boolean;
  error: string | null;
  isEmpty: boolean;
  isFiltered: boolean;
  page: number;
  limit: number;
  setPage: (p: number) => void;
  setLimit: (n: number) => void;
  setPagination: (page: number, limit: number) => void;
  aplicarFiltros: () => void;
  limparFiltros: () => void;
  refetch: () => void;
};

export function useContasPagar(options?: {
  eventoId?: number;
}): UseContasPagarResult {
  const eventoIdFixo = options?.eventoId;
  const [filtros, setFiltros] = useState<ContasPagarFiltros>(() =>
    filtrosPadrao(eventoIdFixo),
  );
  const [filtrosAplicados, setFiltrosAplicados] =
    useState<ContasPagarFiltros>(() => filtrosPadrao(eventoIdFixo));

  const [rawData, setRawData] = useState<ContasPagarApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const page = filtrosAplicados.page ?? 1;
  const limit = filtrosAplicados.limit ?? 20;

  const setPage = useCallback((p: number) => {
    setFiltrosAplicados((prev) => ({
      ...prev,
      page: Math.max(1, p),
    }));
  }, []);

  const setLimit = useCallback((n: number) => {
    const nextLimit = Math.min(100, Math.max(1, n));
    setFiltrosAplicados((prev) => ({
      ...prev,
      limit: nextLimit,
      ...(prev.limit !== nextLimit ? { page: 1 } : {}),
    }));
  }, []);

  const setPagination = useCallback((p: number, n: number) => {
    setFiltrosAplicados((prev) => ({
      ...prev,
      page: Math.max(1, p),
      limit: Math.min(100, Math.max(1, n)),
    }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload: ContasPagarFiltros = {
          ...filtrosAplicados,
          page: filtrosAplicados.page ?? 1,
          limit: filtrosAplicados.limit ?? 20,
        };
        const { data, status } = await listarContasPagar(payload);
        if (cancelled) return;
        if (status === 403) {
          setError("Sem permissão para listar contas a pagar.");
          setRawData(null);
          return;
        }
        if (status === 404) {
          setError(
            "Endpoint de contas a pagar não encontrado. Verifique GET /loja/financeiro/titulos-pagar.",
          );
          setRawData(null);
          return;
        }
        if (status !== 200 || !data) {
          setError("Não foi possível carregar os títulos a pagar.");
          setRawData(null);
          return;
        }
        setRawData(data);
      } catch (e: unknown) {
        if (cancelled) return;
        const msg =
          e instanceof Error ? e.message : "Falha ao carregar contas a pagar.";
        setError(msg);
        setRawData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [filtrosAplicados, tick]);

  const data = useMemo(() => mapContasPagarApiToView(rawData), [rawData]);

  const isEmpty = useMemo(() => {
    if (error) return false;
    return !loading && data != null && data.itens.length === 0;
  }, [error, loading, data]);

  const isFiltered = useMemo(() => {
    const defaults = filtrosPadrao(eventoIdFixo);
    const f = filtrosAplicados;
    return (
      f.status !== defaults.status ||
      f.vencimentoInicio !== defaults.vencimentoInicio ||
      f.vencimentoFim !== defaults.vencimentoFim ||
      f.categoria !== defaults.categoria ||
      f.contexto !== defaults.contexto ||
      f.naturezaDespesa !== defaults.naturezaDespesa ||
      f.centroCustoId !== defaults.centroCustoId ||
      f.fornecedorId !== defaults.fornecedorId ||
      (f.busca ?? "").trim() !== "" ||
      f.apenasVencidos !== defaults.apenasVencidos
    );
  }, [filtrosAplicados, eventoIdFixo]);

  const aplicarFiltros = useCallback(() => {
    setFiltrosAplicados({
      ...filtros,
      page: 1,
      limit: filtros.limit ?? 20,
    });
  }, [filtros]);

  const limparFiltros = useCallback(() => {
    const next = filtrosPadrao(eventoIdFixo);
    setFiltros(next);
    setFiltrosAplicados(next);
  }, [eventoIdFixo]);

  const refetch = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  return {
    filtros,
    setFiltros,
    filtrosAplicados,
    data,
    rawData,
    loading,
    error,
    isEmpty,
    isFiltered,
    page,
    limit,
    setPage,
    setLimit,
    setPagination,
    aplicarFiltros,
    limparFiltros,
    refetch,
  };
}
